import {
  Box,
  Center,
  Checkbox,
  Divider,
  Group,
  LoadingOverlay,
  lighten,
  Stack,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { useField, useForm } from '@mantine/form';
import { type LineCustomSvgLayerProps, ResponsiveLine, type SliceTooltipProps } from '@nivo/line';
import dayjs from 'dayjs';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, type TranslateFunction, tKeysLoose, useTranslation } from 'locales';
import { initial, last } from 'lodash-es';
import { isNullish, omit } from 'remeda';
import {
  type AgedBalanceOverTime,
  useLoadAgedBalanceOverTime,
} from 'shared/hooks/use-load-aged-balance-over-time';
import { useAgedBalanceColors } from 'shared/hooks/utils/use-aged-balance-colors';
import {
  getCurrencySymbolFromCurrencyCode,
  useLocalizedCurrencyFormatter,
  useSafeLocalizedCompactCurrencyFormatter,
} from 'shared/utils/currency';

import { AnalyticsCard } from '../AnalyticsCard';

import { AgedBalanceDateRangeAndExport } from './AgedBalanceDateRangeAndExport';

const Y_TICK_COUNT = 5;
const DAILY_PLOT_LIMIT = 42;

const toPlottedBalances = <Balance,>(balances: ReadonlyArray<[string, Balance]>) =>
  balances.length <= DAILY_PLOT_LIMIT
    ? balances
    : balances.filter(
        ([date], index) => index === 0 || index === balances.length - 1 || dayjs(date).day() === 1,
      );

interface AgedBalanceOverTimeChartProps {
  selectedViewId: string | null;
  titleActions?: React.ReactNode;
  areDateRangeAndExportControlsVisible?: boolean;
}

type LinearFilters = {
  id: string;
  color: string;
  data: Array<{ x: string; y: number; color: string }>;
};

const handleCsvExport = (
  { data }: AgedBalanceOverTime,
  filters: Array<LinearFilters>,
  t: TranslateFunction,
  currencySymbol: string,
) => {
  const headers = ['Date', ...filters.map(filterObj => filterObj.id), 'Total'];

  const exportedData =
    Object.keys(data).length > 40
      ? Object.entries(data).filter((_, index) => index % 7 === 0)
      : Object.entries(data);

  const rows = exportedData.map(([date, { credits, debits }]) => {
    const keyMap = {
      [t(i18nKeys.ANALYTICS.AGED_BALANCE.notDue)]: 'notDue',
      [t(i18nKeys.ANALYTICS.AGED_BALANCE[0])]: '0',
      [t(i18nKeys.ANALYTICS.AGED_BALANCE[30])]: '30',
      [t(i18nKeys.ANALYTICS.AGED_BALANCE[60])]: '60',
      [t(i18nKeys.ANALYTICS.AGED_BALANCE[90])]: '90',
    };

    const values = filters.map(filterObj => {
      const key = keyMap[filterObj.id];
      return `${(parseFloat(String(debits[key as keyof typeof debits])) - parseFloat(String(credits[key as keyof typeof credits]))).toFixed(2)}${currencySymbol}`;
    });

    const total = values.reduce((sum, value) => sum + parseFloat(value), 0).toFixed(2);
    return `"${dayjs(date).format('L')}",${values.join(',')},${total}${currencySymbol}`;
  });

  const allDates = Object.keys(data);
  const csvContent = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });

  const link = document.createElement('a');
  link.setAttribute('href', URL.createObjectURL(blob));
  link.setAttribute('download', `aged_balance_over_time_${allDates[0]}-${last(allDates)}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const LightAreaBorders = ({ series, lineGenerator }: LineCustomSvgLayerProps<LinearFilters>) => (
  <g>
    {series.map(({ id, data, color }) => (
      <path
        key={id}
        d={lineGenerator(data.map(({ position }) => position)) ?? undefined}
        fill="none"
        stroke={lighten(color, 0.4)}
        strokeWidth={1}
      />
    ))}
  </g>
);

const CustomSliceTooltip = ({ slice }: SliceTooltipProps<any>) => {
  const formatter = useLocalizedCurrencyFormatter();
  const total = slice.points[0].data.yStacked;

  return (
    <Box
      w={320}
      p="md"
      bg="white"
      style={{
        border: '1px solid var(--mantine-color-gray-2)',
        borderRadius: 'var(--mantine-radius-sm)',
        // var(--mantine-shadow-md) doesn't work here, hardcoded it
        boxShadow: `0 calc(0.0625rem * var(--mantine-scale))
          calc(0.1875rem * var(--mantine-scale)) rgba(0, 0, 0, 0.05),
        rgba(0, 0, 0, 0.05) 0 calc(0.625rem * var(--mantine-scale))
          calc(0.9375rem * var(--mantine-scale)) calc(-0.3125rem * var(--mantine-scale)),
        rgba(0, 0, 0, 0.04) 0 calc(0.4375rem * var(--mantine-scale))
          calc(0.4375rem * var(--mantine-scale)) calc(-0.3125rem * var(--mantine-scale))`,
      }}
    >
      <Stack align="center">
        <Center>
          <Text mb="sm" fw="bold">
            {dayjs(slice.points[0].data.x).format('LL')}
          </Text>
        </Center>
        <Group>
          <Stack>
            {slice.points.map(point => (
              <Group key={point.id}>
                <Box w={12} h={12} bdrs="50%" bg={point.color} />
                {/* @ts-ignore react 19 */}
                <Text my="0">{point.seriesId}</Text>
              </Group>
            ))}
            <Divider my="xs" w="115%" />
            <Group mt={0}>
              <Box w={12} h={12} bdrs="50%" opacity={0} />
              <Text my="0">Total</Text>
            </Group>
          </Stack>
          <Stack>
            {slice.points.map(point => (
              <Text my="0" fw="bold" key={point.id}>
                {formatter.format(Number(point.data.y))}
              </Text>
            ))}
            <Divider my="xs" />
            <Text mt={0} fw="bold">
              {formatter.format(Number(total))}
            </Text>
          </Stack>
        </Group>
      </Stack>
    </Box>
  );
};

export const AgedBalanceOverTimeChart = ({
  selectedViewId,
  titleActions,
  areDateRangeAndExportControlsVisible = true,
}: AgedBalanceOverTimeChartProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const colors = useAgedBalanceColors();
  const company = useCompanyConfiguration();

  const formatter = useLocalizedCurrencyFormatter();
  const compactFormatter = useSafeLocalizedCompactCurrencyFormatter();

  const startDate = useField({ initialValue: null });
  const endDate = useField({ initialValue: null });

  const { agedBalanceOverTime, isAgedBalanceOverTimeLoading } = useLoadAgedBalanceOverTime({
    viewId: selectedViewId,
    startDate: startDate.getValue(),
    endDate: endDate.getValue(),
  });

  const form = useForm({
    initialValues: {
      '0': true,
      '1': true,
      '2': true,
      '3': true,
      '4': true,
    },
  });

  if (isNullish(agedBalanceOverTime)) {
    return (
      <AnalyticsCard actions={[]} title={t(i18nKeys.ANALYTICS.AGED_BALANCE.CHART_TITLE)}>
        <LoadingOverlay visible={isAgedBalanceOverTimeLoading} />
      </AnalyticsCard>
    );
  }

  const plottedBalances = toPlottedBalances(Object.entries(agedBalanceOverTime.data));
  const allDates = plottedBalances.map(([date]) => date);

  const lines = Object.entries(omit(colors, ['netBalance', 'totalBalance'])).map(
    ([key, color]) => ({
      id: t(tKeysLoose.ANALYTICS.AGED_BALANCE[key]),
      color,
      data: plottedBalances.map(([date, balance]) => ({
        x: date,
        y:
          balance.debits[key as keyof typeof balance.debits] -
          balance.credits[key as keyof typeof balance.credits],
        color,
      })),
    }),
  );

  const linesOrdered = [last(lines)!, ...initial(lines)];
  const indicesToHide = Object.entries(form.values).reduce(
    (acc, [index, flag]) => {
      if (!flag) return [...acc, Number(index)];
      return acc;
    },
    [] as Array<number>,
  );

  const linesFiltered = linesOrdered.filter((_, index) => !indicesToHide.includes(index));
  const isOnlyOneLineEnabled = linesFiltered.length === 1;
  const bottomAxisStep = Math.max(1, Math.floor(allDates.length / 9));
  const today = dayjs().toDate();

  const hasNegativeValues = linesFiltered.some(line => line.data.some(point => point.y < 0));

  return (
    <AnalyticsCard
      actions={
        <>
          {areDateRangeAndExportControlsVisible && (
            <AgedBalanceDateRangeAndExport
              startDateInputProps={startDate.getInputProps()}
              endDateInputProps={endDate.getInputProps()}
              maxDate={today}
              onExport={() =>
                handleCsvExport(
                  agedBalanceOverTime,
                  linesFiltered,
                  t,
                  getCurrencySymbolFromCurrencyCode(company.currency),
                )
              }
            />
          )}
          {titleActions}
        </>
      }
      title={t(i18nKeys.ANALYTICS.AGED_BALANCE.CHART_TITLE)}
    >
      <LoadingOverlay visible={isAgedBalanceOverTimeLoading} />
      <Box w="100%" h={500}>
        <ResponsiveLine
          yScale={{
            stacked: true,
            type: 'linear',
            // Without this condition there is always one additional line under 0 even when all the values are positive
            min: hasNegativeValues ? 'auto' : 0,
          }}
          colors={d => d.color}
          margin={{ top: 10, left: 85, bottom: 60, right: 50 }}
          data={linesFiltered}
          isInteractive
          enableSlices="x"
          enableGridX={false}
          gridYValues={Y_TICK_COUNT}
          enablePoints={false}
          yFormat={val => formatter.format(Number(val))}
          axisBottom={{
            tickSize: 6,
            tickPadding: 14,
            tickRotation: -25,
            tickValues: (() => allDates.filter((_, index) => index % bottomAxisStep === 0))(),
            format: date => dayjs(date).format('L'),
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 20,
            tickValues: Y_TICK_COUNT,
            format: value => compactFormatter.format(value),
          }}
          curve="monotoneX"
          layers={[
            'grid',
            'markers',
            'areas',
            LightAreaBorders,
            'axes',
            'crosshair',
            'points',
            'slices',
            'mesh',
            'legends',
          ]}
          enableArea
          areaOpacity={1}
          sliceTooltip={CustomSliceTooltip}
          theme={{
            axis: {
              ticks: {
                line: { transform: 'translateY(-3px)' },
              },
            },
            grid: {
              line: {
                stroke: theme.colors.gray[3],
                strokeDasharray: 7,
                strokeDashoffset: 15,
              },
            },
            labels: {
              text: {
                fontSize: 15,
                fontWeight: 500,
                fill: theme.colors.gray[6],
              },
            },
            text: {
              fontSize: 14,
              fill: theme.colors.gray[6],
            },
          }}
        />
      </Box>
      <Group w="100%" mb="md" justify="center" gap="xl" style={{ marginTop: '2rem' }}>
        {linesOrdered.map((line, index) => (
          <Checkbox
            size="xs"
            style={{ cursor: 'pointer' }}
            key={index}
            {...form.getInputProps(String(index), { type: 'checkbox' })}
            label={line.id}
            color={line.color}
            disabled={
              isOnlyOneLineEnabled && form.values[String(index) as keyof typeof form.values]
            }
          />
        ))}
      </Group>
    </AnalyticsCard>
  );
};
